import posthog from "posthog-js"

export type BookMockupAnalyticsEvent =
  | "book mockup engaged"
  | "book mockup photo export failed"
  | "book mockup photo exported"
  | "book mockup saved"
  | "book mockup share copied"
  | "book mockup share created"
  | "book mockup share revoked"
  | "book mockup view selected"
  | "book mockup viewer ready"

type AnalyticsValue = boolean | number | string

export type BookMockupAnalyticsProperties = Record<
  string,
  AnalyticsValue | AnalyticsValue[]
>

export interface BookMockupAnalyticsMessage {
  type: "bookmock:analytics"
  event: BookMockupAnalyticsEvent
  properties: BookMockupAnalyticsProperties
}

export interface PostHogConfig {
  api_host?: string
  enabled: boolean
  project_api_key?: string
}

let initialized = false

export function initializePostHog(
  config: PostHogConfig | undefined,
  user?: { id: number; name?: string } | null,
) {
  if (!config?.enabled) return

  if (!config.project_api_key) {
    if (import.meta.env.DEV) {
      throw new Error("PostHog is enabled but its project API key is missing.")
    }
    return
  }

  if (!initialized) {
    posthog.init(config.project_api_key, {
      api_host: config.api_host,
      autocapture: false,
      capture_exceptions: true,
      capture_pageleave: false,
      capture_pageview: false,
      defaults: "2026-01-30",
      disable_session_recording: true,
      mask_all_element_attributes: true,
      mask_all_text: true,
      person_profiles: "identified_only",
    })
    initialized = true
  }

  if (user) {
    posthog.identify(String(user.id), { name: user.name })
  }
}

function viewerMode() {
  if (window.parent !== window) return "embed"
  if (document.documentElement.dataset.publicMockup === "true") return "public"
  return "editor"
}

function parentOrigin() {
  if (!document.referrer) return "*"

  try {
    return new URL(document.referrer).origin
  } catch {
    return "*"
  }
}

export function captureBookMockupEvent(
  event: BookMockupAnalyticsEvent,
  properties: BookMockupAnalyticsProperties = {},
) {
  const contextualProperties = {
    ...properties,
    is_mobile: window.matchMedia("(max-width: 767px)").matches,
    viewer_mode: viewerMode(),
  }

  if (window.parent !== window) {
    const message: BookMockupAnalyticsMessage = {
      type: "bookmock:analytics",
      event,
      properties: contextualProperties,
    }
    window.parent.postMessage(message, parentOrigin())
    return
  }

  if (initialized) posthog.capture(event, contextualProperties)
}
